import React, { useState, useEffect, useCallback, useRef } from 'react';

// --- CONFIGURACIÓN DE ALMACENAMIENTO LOCAL ---
const LOCAL_STORAGE_KEY = 'profileData_v2';
const MAX_FILE_SIZE_BYTES = 500 * 1024; // Límite de 500 KB para Base64 en localStorage

// --- ICONOS MODERNOS (SVG) ---

const IconWrapper = ({ children, className, ...props }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className} 
        {...props}
    >
        {children}
    </svg>
);

const UserIcon = (props) => (
    <IconWrapper {...props}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></IconWrapper>
);

const MailIcon = (props) => (
    <IconWrapper {...props}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.83 1.83 0 0 1-2.06 0L2 7"/></IconWrapper>
);

const StethoscopeIcon = (props) => (
    <IconWrapper {...props}><path d="m3 21 6-6"/><path d="m19 19-3-3"/><path d="M14.88 16.88a3 3 0 0 0 2.12-.88l1.41-1.41a2 2 0 0 0 0-2.83L14 8"/><path d="m11.21 15.21-1.42 1.42a3 3 0 0 1-4.24 0L4.34 16.81"/><path d="M15 11h-3a3 3 0 0 0-3 3v3h6v-3a3 3 0 0 0-3-3Z"/></IconWrapper>
);

const UploadIcon = (props) => (
    <IconWrapper {...props}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></IconWrapper>
);

const SaveIcon = (props) => (
    <IconWrapper {...props}><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></IconWrapper>
);

const CheckCircleIcon = (props) => (
    <IconWrapper {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></IconWrapper>
);

const XCircleIcon = (props) => (
    <IconWrapper {...props}><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></IconWrapper>
);

// Placeholder SVG limpio para la imagen de perfil
const DEFAULT_PLACEHOLDER_BASE64 = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQwIiBoZWlnaHQ9IjI0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMjQwIDI0MCIgZmlsbD0ibm9uZSI+CiAgPGNpcmNsZSBjeD0iMTIwIiBjeT0iMTIwIiByPSIxMTgiIGZpbGw9IiNmZmYiIHN0cm9rZT0iIzZkNTkxZiIgc3Ryb2tlLXdpZHRoPSI0Ii8+CiAgPHBhdGggZmlsbD0iIzQ2NURFOCIgZD0iTTE2MCAxMzBjMC0yMi4wOS0xNy45MS00MC00MC00MHMtNDAgMTcuOTEtNDAgNDBjMCAyMi4wOSAxNy45MSA0MCA0MCA0MHM0MC0xNy45MSA0MC00MFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAtMzUpIi8+CiAgPHBhdGggZmlsbD0iIzQ2NURFOCIgZD0iTTE4MCAxOTBjLTMwLTMzLTYwLTM1LTYwLTc0IDAtMzkgMTgtNjMgNDAtNjNIMTUwYzIyIDAgNDAgMjQgNDAgNjMgMCAzOS0zMCA0MS02MCA3NFoiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAtLTExNSkgc2NhbGUoMS41KSIvPgo8L3N2Zw==';


// --- ESTRUCTURA DE DATOS PREDETERMINADA ---
const DEFAULT_PROFILE = {
    name: 'Dra. Mariana Torres',
    email: 'mariana@medisys.mx',
    specialty: 'Veterinaria general',
    photoUrl: DEFAULT_PLACEHOLDER_BASE64, 
};

// --- COMPONENTE PRINCIPAL ---
export default function App() {                                                                                              
    // --- ESTADOS ---
    const [profile, setProfile] = useState(DEFAULT_PROFILE);
    const [isSaving, setIsSaving] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false); 
    const [notification, setNotification] = useState({ message: '', type: '' }); 
    
    // Referencia para el input de archivo
    const fileInputRef = useRef(null); 

    // --- FUNCIÓN DE NOTIFICACIÓN ---
    const showNotification = useCallback((message, type) => {
        setNotification({ message, type });
        setTimeout(() => setNotification({ message: '', type: '' }), 4000);
    }, []);

    // --- CARGA DE DATOS DESDE LOCALSTORAGE AL MONTAR ---
    useEffect(() => {
        try {
            const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                // Usar el placeholder por defecto si la URL de la foto está vacía
                setProfile({ 
                    ...parsedData, 
                    photoUrl: parsedData.photoUrl || DEFAULT_PLACEHOLDER_BASE64 
                });
            } else {
                // Si no hay datos, guarda el perfil por defecto
                localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(DEFAULT_PROFILE));
            }
        } catch (e) {
            console.error("Error al cargar datos:", e);
            showNotification("Error al cargar el perfil. Se restableció la configuración predeterminada.", 'error');
            // Forzar el estado por defecto si falla la carga
            setProfile(DEFAULT_PROFILE);
        }
        setIsDataLoaded(true); 
    }, [showNotification]);

    // --- MANEJADOR DE CARGA DE IMAGEN LOCAL (Base64) ---
    const handleImageUpload = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > MAX_FILE_SIZE_BYTES) { 
            showNotification(`La imagen es demasiado grande (máx ${Math.round(MAX_FILE_SIZE_BYTES / 1024)}KB).`, 'error');
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setProfile(prev => ({ ...prev, photoUrl: reader.result }));
            showNotification('Imagen cargada localmente. Presiona "Guardar Cambios" para actualizar.', 'success');
        };
        reader.onerror = () => {
            showNotification('Error al leer el archivo de imagen.', 'error');
        };
        reader.readAsDataURL(file);

    }, [showNotification]);

    // --- MANEJADOR DE CAMBIOS EN LOS CAMPOS ---
    const handleChange = useCallback((e) => {
        setProfile(prev => ({ ...prev, [e.target.name]: e.target.value }));
    }, []);

    // --- FUNCIÓN PARA GUARDAR EN LOCALSTORAGE ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        
        // --- VALIDACIONES ---
        if (!profile.name.trim()) {
            showNotification('El nombre no puede estar vacío.', 'error');
            setIsSaving(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!profile.email.trim() || !emailRegex.test(profile.email)) {
            showNotification('Ingresa un correo electrónico válido.', 'error');
            setIsSaving(false);
            return;
        }

        if (!profile.specialty.trim()) {
            showNotification('La especialidad no puede estar vacía.', 'error');
            setIsSaving(false);
            return;
        }

        // --- GUARDAR EN LOCALSTORAGE ---
        try {
            // Guarda el objeto completo del perfil, incluyendo la cadena Base64
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
            showNotification('Cambios guardados correctamente.', 'success');
        } catch (error) {
            console.error("Error al guardar:", error);
            showNotification('Error al guardar los cambios. Asegúrate de que tu navegador lo soporte.', 'error');
        }
        
        // Simular un poco de latencia para UX
        setTimeout(() => setIsSaving(false), 500);
    };

    // --- COMPONENTE DE NOTIFICACIÓN ---
    const NotificationToast = () => {
        if (!notification.message) return null;

        const baseClasses = "fixed top-4 left-1/2 transform -translate-x-1/2 p-4 rounded-xl shadow-2xl z-50 text-white font-semibold flex items-center space-x-2 transition-all duration-300 ease-in-out";
        const typeClasses = notification.type === 'success'
            ? 'bg-green-600'
            : 'bg-red-600';
        
        const Icon = notification.type === 'success' ? CheckCircleIcon : XCircleIcon;

        return (
            <div className={`${baseClasses} ${typeClasses} animate-bounce-in`}>
                <Icon className="w-6 h-6"/>
                <span>{notification.message}</span>
            </div>
        );
    };

    // --- RENDERIZADO PRINCIPAL ---
    return (
        <div className="min-h-screen bg-gray-100 flex items-start justify-center p-4 sm:p-8" style={{ fontFamily: 'Inter, sans-serif' }}>
            <NotificationToast />

            <div className="w-full max-w-xl mt-10">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8 text-center border-b pb-4">
                     Perfil Médico
                </h1>

                {/* Tarjeta de Perfil */}
                <div className="bg-white p-6 sm:p-10 rounded-3xl shadow-2xl border border-gray-100">

                    {/* Encabezado y Foto */}
                    <div className="flex flex-col items-center gap-6 border-b pb-6 mb-6">
                        <div className="relative flex flex-col items-center gap-3">
                            <img
                                src={profile.photoUrl}
                                alt="Foto de perfil"
                                className="w-32 h-32 rounded-full object-cover ring-4 ring-indigo-500 ring-offset-4 shadow-lg transition duration-300 hover:ring-indigo-600 cursor-pointer"
                                onClick={() => fileInputRef.current?.click()}
                            />
                            
                            {/* Input de archivo real (oculto) */}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/png, image/jpeg"
                                style={{ display: 'none' }}
                            />

                            {/* Botón visible de Subir Foto */}
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="text-sm font-medium text-white bg-indigo-500 py-2 px-4 rounded-full hover:bg-indigo-600 transition duration-150 shadow-md flex items-center space-x-1"
                            >
                                <UploadIcon className="w-4 h-4" />
                                <span>Subir Foto</span>
                            </button>
                            <p className="text-xs text-gray-500 italic">Máx. 500 KB (para persistencia local)</p>
                        </div>

                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900">{profile.name}</h2>
                            <p className="text-indigo-600 font-medium">{profile.specialty}</p>
                            <p className="text-gray-500 text-sm">{profile.email}</p>
                        </div>
                    </div>

                    {/* Formulario de Edición */}
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        
                        {/* Campo Nombre */}
                        <div className="relative">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre Completo:</label>
                            <div className="flex items-center border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 transition duration-150">
                                <UserIcon className="w-5 h-5 ml-3 text-gray-400" />
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    value={profile.name}
                                    onChange={handleChange}
                                    placeholder="Nombre completo del profesional"
                                    className="w-full px-3 py-2.5 bg-transparent rounded-r-lg focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Campo Correo */}
                        <div className="relative">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Correo Electrónico:</label>
                            <div className="flex items-center border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 transition duration-150">
                                <MailIcon className="w-5 h-5 ml-3 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    value={profile.email}
                                    onChange={handleChange}
                                    placeholder="email@ejemplo.com"
                                    className="w-full px-3 py-2.5 bg-transparent rounded-r-lg focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Campo Especialidad */}
                        <div className="relative">
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Especialidad/Título:</label>
                            <div className="flex items-center border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 transition duration-150">
                                <StethoscopeIcon className="w-5 h-5 ml-3 text-gray-400" />
                                <input
                                    type="text"
                                    name="specialty"
                                    required
                                    value={profile.specialty}
                                    onChange={handleChange}
                                    placeholder="Especialidad médica o título"
                                    className="w-full px-3 py-2.5 bg-transparent rounded-r-lg focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Botón guardar */}
                        <button
                            type="submit"
                            disabled={isSaving || !isDataLoaded}
                            className={`w-full flex items-center justify-center space-x-2 py-3 mt-8 font-bold rounded-xl shadow-lg transition duration-300 transform active:scale-95
                                ${isSaving || !isDataLoaded 
                                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed opacity-75' 
                                    : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-xl'
                                }`}
                        >
                            {isSaving ? (
                                <>
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Guardando...</span>
                                </>
                            ) : (
                                <>
                                    <SaveIcon className="w-5 h-5" />
                                    <span>Guardar Cambios</span>
                                </>
                            )}
                        </button>
                        
                        <p className="text-xs text-center text-gray-400 pt-4">Medical Cleth.</p>
                    </form>
                </div>
            </div>
        </div>
    );
}